import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import orderBy from "lodash/orderBy";

const identity = _ => _;

export default function computeNoteHistory(
	subDocs,
	{
		timezone = "local",
		translate = identity,
	} = {} // options
) {
	const today = DateTime.now().setZone( timezone ).toFormat( "yyyy-MM-dd" );
	const months = new Set();
	const weeks = new Set();

	// eslint-disable-next-line max-statements
	const appendHistory = ( history, subDoc ) => {
		const { subDocId, date, title, recurrenceId, } = subDoc;
		// TODO: change once subDoc.date is converted to a luxon object
		const subDocDate = DateTime.fromISO( date.toISOString(), { zone: timezone, } );

		//
		// monthly heading
		//
		const monthStart = subDocDate.startOf( "month" ).toUnixInteger();

		if ( !months.has( monthStart ) ) {
			months.add( monthStart );

			const thisFormattedMonth = subDocDate.toFormat( "MMMM yyyy" ); // June 2023

			history.push( {
				uiKey: uuidv4(),
				uiDiscriminator: "heading:month",
				// set to the END of the month because the sort order is REVERSED in the menu
				sortKey: subDocDate
					.endOf( "month" )
					.endOf( "day" )
					.toUnixInteger(),
				titleLabel: thisFormattedMonth,
				dateLabel: thisFormattedMonth,
			} );
		}

		//
		// weekly heading
		//
		const startOfWeek = subDocDate.startOf( "week" )
			.minus( { days: 1, } ); // force the week to be Sun-Sat
		const startOfWeekTimestamp = startOfWeek.toUnixInteger();

		if ( !weeks.has( startOfWeekTimestamp ) ) {
			weeks.add( startOfWeekTimestamp );

			const startOfWeekDay = startOfWeek
				.toFormat( "d" ); // 18
			const startOfWeekMonth = startOfWeek.toFormat( "MMMM" ); // June

			const endOfWeek = subDocDate.endOf( "week" )
				.minus( { days: 1, } ); // force the week to be Sun-Sat
			const endOfWeekDay = endOfWeek
				.toFormat( "d" ); // 24
			let endOfWeekMonth = endOfWeek.toFormat( "MMMM" ); // June

			if ( endOfWeekMonth === startOfWeekMonth ) {
				endOfWeekMonth = "";
			}

			const startOfWeekLabel = `${ startOfWeekMonth } ${ startOfWeekDay }`;
			const endOfWeekLabel = `${ endOfWeekMonth } ${ endOfWeekDay }`.trim();
			const thisFormattedWeek = `${ startOfWeekLabel } - ${ endOfWeekLabel }`; // June 18 - 24 (or June 25 - July 1)

			history.push( {
				uiKey: uuidv4(),
				uiDiscriminator: "heading:week",
				// set to the END of the week because the sort order is REVERSED in the menu
				sortKey: subDocDate
					.endOf( "week" )
					.endOf( "day" )
					.toUnixInteger(),
				titleLabel: thisFormattedWeek,
				dateLabel: thisFormattedWeek,
			} );
		}

		//
		// note entry
		//
		const formattedDay = subDocDate.toFormat( "yyyy-MM-dd" ); // 2022-05-03
		const isToday = ( formattedDay === today );

		history.push( {
			uiKey: uuidv4(),
			uiDiscriminator: "entry",
			sortKey: subDocDate.toUnixInteger(),
			shortDate: formattedDay,
			subDocId,
			isToday,
			recurrenceId,
			titleLabel: title,
			dateLabel: isToday ?
				translate( "todayLabel", {
					date: subDocDate.toFormat( "MMM d, yyyy :: t" ), // Today, May 3, 2023 :: 1:30 PM
				} ) :
				subDocDate.toFormat( "ccc, MMM d, yyyy :: t" ), // Tue, Tue, May 3, 2023 :: 1:30 PM
		} );

		return history;
	};

	const uiNoteHistory = subDocs.reduce( appendHistory, [] );

	return orderBy( uiNoteHistory, [ "sortKey", ], [ "desc", ] );
}
